import { CheckCircle2, Upload } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import type { ExternalApiError } from '@/api/external';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { ExternalDocumentUploadForm } from '@/components/forms/commercial/credit-analysis/document-upload-external-public-form/external-document-upload-form';
import { ExternalLinkError } from '@/components/forms/commercial/credit-analysis/document-upload-external-public-form/external-link-error';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type AccountType,
  fieldsByAccountType,
  normalizeAccountTypeForCreditAnalysis,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import {
  useExternalDocumentState,
  useUploadExternalDocuments,
  useValidateExternalLink,
} from '@/hooks/use-external-portal';
import { useToast } from '@/hooks/use-toast';

export function ExternalDocumentUploadPage() {
  const { linkId } = useParams<{ linkId: string }>();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const { toast } = useToast();

  // Validate link
  const {
    data: validation,
    isLoading: isValidating,
    error: validationError,
  } = useValidateExternalLink(linkId || '', email);

  // Get document state
  const { data: documentState, isLoading: isLoadingDocuments } = useExternalDocumentState(
    linkId || '',
    email,
  );

  // Upload mutation
  const _uploadMutation = useUploadExternalDocuments();

  // Success state after upload
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Determine account type from validation data
  const accountType = useMemo<AccountType>(() => {
    if (!validation?.account) return 'ME';
    return normalizeAccountTypeForCreditAnalysis(
      validation.account.accountType,
      validation.account.simeiSince,
      validation.account.document?.type,
    );
  }, [validation]);

  // Map document state to document slots for the form
  const documentSlots = useMemo(() => {
    if (!documentState?.documents) return [];

    const fields = fieldsByAccountType[accountType];
    const slotMap = new Map<
      string,
      {
        status: 'approved' | 'rejected' | 'pending' | 'none';
        rejectionReason?: string;
        uploadedFileName?: string;
        uploadedAt?: string;
      }
    >();

    // Build a map from document uploads
    documentState.documents.forEach((doc) => {
      if (doc.fieldId) {
        slotMap.set(doc.fieldId, {
          status: doc.reviewStatus || 'none',
          rejectionReason: doc.rejectionReason || undefined,
          uploadedFileName: doc.fileName,
          uploadedAt: doc.createdAt,
        });
      }
    });

    // Create slots for all fields
    return fields.map((field) => {
      const slotData = slotMap.get(field.key);
      return {
        fieldId: field.key,
        label: field.label,
        required: field.required,
        status: slotData?.status || 'none',
        rejectionReason: slotData?.rejectionReason,
        uploadedFileName: slotData?.uploadedFileName,
        uploadedAt: slotData?.uploadedAt,
      };
    });
  }, [documentState, accountType]);

  // Handle successful upload
  const handleUploadSuccess = useCallback(() => {
    setUploadSuccess(true);
    toast({
      variant: 'success',
      title: 'Documentos enviados com sucesso!',
      description: 'Os documentos foram enviados para análise.',
    });
  }, [toast]);

  // Extract error code from validation error
  const getErrorCode = (): 'EXPIRED' | 'INVALID' | 'EMAIL_NOT_ALLOWED' | 'NOT_FOUND' => {
    if (!validationError) return 'INVALID';

    const error = validationError as { response?: { data?: ExternalApiError } };
    const code = error.response?.data?.code;

    if (
      code === 'EXPIRED' ||
      code === 'INVALID' ||
      code === 'EMAIL_NOT_ALLOWED' ||
      code === 'NOT_FOUND'
    ) {
      return code;
    }

    return 'INVALID';
  };

  // Loading state
  if (isValidating || isLoadingDocuments) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4">
                <AnimatedCubeIcon size={48} className="text-gray-400" />
              </div>
              <CardTitle>Verificando link...</CardTitle>
              <CardDescription>Por favor, aguarde enquanto validamos seu acesso.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (validationError || !validation) {
    return <ExternalLinkError errorCode={getErrorCode()} />;
  }

  // Success state after upload
  if (uploadSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Documentos enviados!</CardTitle>
              <CardDescription>
                Seus documentos foram enviados com sucesso para análise.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-xs text-gray-500 mt-6">
                {new Date().getFullYear()} BWSIOT &copy;
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Document upload form
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12 lg:px-8 bg-gray-50">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <AnimatedCubeIcon size={48} className="mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Envio de Documentos</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload de Documentos
            </CardTitle>
            <CardDescription>
              Envie os documentos solicitados para prosseguir com sua análise de crédito
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ExternalDocumentUploadForm
              linkId={linkId || ''}
              email={email}
              accountType={accountType}
              documentSlots={documentSlots}
              onSuccess={handleUploadSuccess}
            />
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          Link ID: {linkId} | {new Date().getFullYear()} BStock &copy;
        </p>
      </div>
    </div>
  );
}
